import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import BlackHole from "@/studio/components/BlackHole";
import Magnetic from "@/studio/components/Magnetic";
import { scrollToId } from "@/studio/lib/useStudioScroll";

const ease = [0.16, 1, 0.3, 1];

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, ease, delay: 0.2 + i * 0.08 },
  }),
};

/**
 * Magnetic title — subtly tracks the cursor (≤ 10px) with damped spring.
 * Letters are individually rendered so they can spread on scroll.
 */
function MagneticTitle({ text = "PARABOLUM", spread }) {
  const ref = useRef(null);
  const tx = useMotionValue(0);
  const ty = useMotionValue(0);
  const sx = useSpring(tx, { stiffness: 90, damping: 16, mass: 0.6 });
  const sy = useSpring(ty, { stiffness: 90, damping: 16, mass: 0.6 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const dx = (e.clientX - cx) / window.innerWidth;
      const dy = (e.clientY - cy) / window.innerHeight;
      tx.set(dx * 14);
      ty.set(dy * 8);
    };
    const onLeave = () => { tx.set(0); ty.set(0); };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerleave", onLeave);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerleave", onLeave);
    };
  }, [tx, ty]);

  // letter tracking driven by scroll spread (0..1 → 0..0.07em)
  const letterSpacing = useTransform(spread, [0, 1], ["-0.045em", "0.020em"]);

  return (
    <motion.span
      ref={ref}
      style={{ x: sx, y: sy, letterSpacing }}
      className="chroma block"
      data-text={text}
    >
      {text}
    </motion.span>
  );
}

export default function Hero({ onContact, ready = true, titleReady = true }) {
  const sectionRef = useRef(null);
  const [accel, setAccel] = useState(0);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  // 0 at top → 1 by bottom of hero
  const spread = useTransform(scrollYProgress, [0, 1], [0, 1]);
  const holeY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const holeScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  // particle acceleration follows scroll velocity (0..1)
  useEffect(() => {
    let last = 0;
    let lastT = performance.now();
    let raf;
    const tick = () => {
      const now = performance.now();
      const y = window.scrollY;
      const dy = Math.abs(y - last);
      const dt = Math.max(1, now - lastT);
      const v = Math.min(1, dy / dt / 4);
      setAccel((prev) => prev + (v - prev) * 0.18);
      last = y;
      lastT = now;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <section
      ref={sectionRef}
      id="top"
      className="relative min-h-[100svh] w-full overflow-hidden"
    >
      {/* Brand black hole — mounted after loader, parallax + scroll pulse */}
      {ready && (
        <div className="pointer-events-none absolute right-[-4%] top-1/2 z-0 hidden h-[62vh] w-[48%] -translate-y-1/2 md:block lg:right-[-2%] lg:w-[44%]">
          <motion.div
            initial={{ opacity: 0, scale: 0.82 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.6, ease, delay: 0.1 }}
            style={{ y: holeY, scale: holeScale }}
            className="h-full w-full"
          >
            <BlackHole
              className="h-full w-full"
              glow
              parallax={14}
              priority
              scrollPulse={accel}
            />
          </motion.div>
        </div>
      )}

      {/* Mobile black hole — keeps the brand symbol present on small screens */}
      {ready && (
        <div className="pointer-events-none absolute left-1/2 top-[14%] z-0 h-[30vh] w-[82%] -translate-x-1/2 md:hidden">
          <motion.div
            initial={{ opacity: 0, scale: 0.86 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, ease, delay: 0.1 }}
            className="h-full w-full"
          >
            <BlackHole className="h-full w-full" glow parallax={0} priority />
          </motion.div>
        </div>
      )}

      <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-[1500px] flex-col justify-end px-5 pb-20 pt-24 md:px-10 md:pb-28 md:pt-28">
        {/* headline */}
        <div>
          <h1 className="display-hero t-mega" data-hero-anchor="parabolum">
            <motion.span
              initial={{ opacity: 0 }}
              animate={titleReady ? { opacity: 1 } : { opacity: 0 }}
              transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
              className="block"
            >
              <MagneticTitle text="PARABOLUM" spread={spread} />
            </motion.span>
          </h1>

          <div className="mt-6 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
            <div className="max-w-md">
              <div className="ff-display text-2xl font-semibold leading-[1.05] tracking-tight sm:text-3xl">
                {["Websites.", "Platforms.", "AI Systems."].map((w, i) => (
                  <span key={w} className="reveal-mask">
                    <motion.span
                      variants={line}
                      custom={i + 1}
                      initial="hidden"
                      animate={ready ? "show" : "hidden"}
                      className="block"
                    >
                      {w}
                    </motion.span>
                  </span>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-5 md:items-end">
              <motion.p
                initial={{ opacity: 0, y: 14 }}
                animate={ready ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 }}
                transition={{ duration: 0.9, ease, delay: ready ? 0.7 : 0 }}
                className="ff-ui max-w-xs text-[14px] leading-relaxed text-[var(--ink-soft)] md:text-right"
              >
                Independent digital studio.
                <br />
                Engineered end-to-end.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={ready ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 }}
                transition={{ duration: 0.9, ease, delay: ready ? 0.85 : 0 }}
                className="flex items-center gap-3"
              >
                <Magnetic>
                  <button
                    onClick={() => scrollToId("work")}
                    data-cursor="big"
                    className="btn-ink"
                  >
                    View work
                  </button>
                </Magnetic>
                <Magnetic>
                  <button onClick={onContact} data-cursor="big" className="btn-ghost">
                    Let&apos;s talk
                  </button>
                </Magnetic>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
